import { findFittedCup, findFittedStem } from '@/views/3d-planner/api/fittedComponents';
import { Matrix4, Vector3 } from 'three';
import { matrixFromApi } from '@/domain/geometry/apiMatrix';
import { positionalPart } from '@/domain/geometry/matrix';
import { matrix4FromBasis } from '@/domain/geometry/basis';
import { lpsIdentityCS } from '@/domain/anatomy/LPS';
import type { PlannerState } from '@/views/3d-planner/store/plannerState';
import { assertNonNull } from '@/util/assert';
import { logger } from '@/util/logger';
import type { NumberArray3 } from '@/domain/geometry/apiVector';
import { vector3 } from '@/domain/geometry/vector3';
import { logValidation } from '@/views/3d-planner/operations/logValidation';
import {
    formatMatrixBasis,
    formatVector,
    indent,
    joinIndented,
} from '@/domain/geometry/formatMath';
import { computeHeadUrl } from '@/views/3d-planner/fitting/componentUrls';
import { useAppErrorStore } from '@/stores/appErrorStore';
import type { ApiFittedStem } from '@/api/fittedComponents/fittedStem';
import type { ApiFittedCup } from '@/api/fittedComponents/fittedCup.types';

const log = logger();

/** An axis the stem is rotated around for manual stem positioning */
export type FittedStemAxis = {
    position: Vector3;
    direction: Vector3;
};

export type FittedStemAxes = {
    /** The neck axis used for extension-flexion rotation, in stem-group space */
    neckAxis: FittedStemAxis;

    /** The shaft axis used for retroversion-anteversion rotation, in stem-group space */
    shaftAxis: FittedStemAxis;

    /** The posterior-anterior axis used for varus-valgus rotation, in stem-group space */
    paAxis: FittedStemAxis;
};

/**
 * A representation of the fitted stem that is derived from fitted-stem data on the API.
 *
 * It gives the fitted-head transform, as well as various features of the stem expressed in
 * stem-group space, and is used for calculating stem placement.
 */
export type FittedStem = {
    /**
     * The fitted-head-transform, in CT/world space. It is *positioned* at the fitted-head
     * centre but *aligned* to the femoral coordinate system.
     *
     * In a 'retracted' arrangement when the stem and femur have not been translated to
     * dock the head into the cup:
     * - It is the transform of the femoral-group
     * - The transform of the stem-group is found by multiplying it to the manual-stem-transform
     * - If there is no manual-stem-transform it is the same as the stem-group transform
     * */
    transform: Matrix4;

    /** The stem transform, in stem-group space */
    stemLocal: Matrix4;

    /** The head transform, in stem-group space */
    headLocal: Matrix4;

    /** The bearing transform, in stem-group space */
    bearingLocal: Matrix4 | null;

    /**
     * Axes of the stem represented in stem-space, used for manual stem positioning.
     *
     * Old cases on the API may not have the axes needed for manual stem positioning, in
     * which case this value will be null
     * */
    axesLocal: FittedStemAxes | null;

    /**
     * The neck axis direction, in stem-group space. Unlike {@link axesLocal}, this does not
     * depend on `medial_pivot_point` — `neck_axis` is a base API field present on every stem,
     * so this is never null.
     */
    neckAxisDirection: Vector3;

    resectionPlaneLocal: {
        normal: Vector3;
        origin: Vector3;
        x: Vector3;
        y: Vector3;
    };
};

export function computeFittedStem(state: PlannerState): FittedStem | null {
    try {
        if (!state.fittedComponents || !state.catalog || !state.template) {
            return null;
        }
        const apiStem = findFittedStem(
            state.fittedComponents,
            state.catalog,
            state.template.stemUrl,
            computeHeadUrl(state),
        );
        const headTransform = matrixFromApi(apiStem.head.tmatrix);
        const transform = matrix4FromBasis(
            state.femoralFeatures?.stemTranslationBasis ?? lpsIdentityCS(),
            positionalPart(headTransform),
        );

        const apiCup = findFittedCup(state.fittedComponents, state.catalog, {
            cupUrl: state.template.cupUrl,
            linerUrl: state.template.linerUrl,
            bearingUrl: state.template.bearingUrl,
        });
        const bearingTransform = resolveBearingTransform(
            apiStem,
            apiCup,
            state.template.dualMobility,
        );

        // Matrix used to transform world/CT space to stem-group local space
        const toStemGroup = transform.clone().invert();
        const neckAxisDirection = vector3(apiStem.neck_axis).transformDirection(toStemGroup);

        const result: FittedStem = {
            transform,
            // When the stem-group is at the fitted-head transform the stem and
            // head meshes should be at their fitted transforms.
            // This lets us calculate their relative transformations.
            stemLocal: toStemGroup.clone().multiply(matrixFromApi(apiStem.tmatrix)),
            headLocal: toStemGroup.clone().multiply(headTransform),
            bearingLocal: bearingTransform ? toStemGroup.clone().multiply(bearingTransform) : null,
            axesLocal: resolveAxesLocal(apiStem, neckAxisDirection, toStemGroup),
            neckAxisDirection,
            resectionPlaneLocal: resolveResectionPlane(apiStem, toStemGroup),
        };

        _logFittedStem(result);

        return result;
    } catch (error) {
        useAppErrorStore().handleError(error);
        return null;
    }
}

/**
 * Null when the template is not dual-mobility. A dual-mobility template with no fitted bearing is
 * inconsistent data, so this throws into the caller's catch.
 */
function resolveBearingTransform(
    apiStem: ApiFittedStem,
    apiCup: ApiFittedCup,
    dualMobility: boolean,
): Matrix4 | null {
    if (!dualMobility) {
        return null;
    }
    if (!apiCup.bearing) {
        throw Error('Dual-mobility is set on template but fitted-cup has no bearing transform');
    }
    return computeBearingTransform(apiStem, apiCup);
}

/**
 * Null on an old case that predates these axes. `neckAxisDirection` is passed in, not recomputed,
 * so it stays the instance `FittedStem.neckAxisDirection` exposes.
 */
function resolveAxesLocal(
    apiStem: ApiFittedStem,
    neckAxisDirection: Vector3,
    toStemGroup: Matrix4,
): FittedStemAxes | null {
    if (!apiStem.medial_pivot_point) {
        log.warn('Stem representation is missing stem-transformation data (old case?)');
        return null;
    }

    const axisToStemGroup = (position: NumberArray3, direction: NumberArray3): FittedStemAxis => ({
        position: vector3(position).applyMatrix4(toStemGroup),
        direction: vector3(direction).transformDirection(toStemGroup),
    });

    return {
        neckAxis: {
            position: vector3(apiStem.medial_pivot_point).applyMatrix4(toStemGroup),
            direction: neckAxisDirection,
        },
        shaftAxis: axisToStemGroup(apiStem.shaft_axis_point, apiStem.shaft_axis_direction),
        paAxis: axisToStemGroup(apiStem.medial_pivot_point, apiStem.pa_axis),
    };
}

/** The resection plane in stem-group space: origin as a position, normal/x/y as directions. */
function resolveResectionPlane(
    apiStem: ApiFittedStem,
    toStemGroup: Matrix4,
): FittedStem['resectionPlaneLocal'] {
    const plane = apiStem.resection_plane;
    return {
        origin: vector3(plane.origin).applyMatrix4(toStemGroup),
        normal: vector3(plane.normal).transformDirection(toStemGroup),
        x: vector3(plane.x).transformDirection(toStemGroup),
        y: vector3(plane.y).transformDirection(toStemGroup),
    };
}

function _logFittedStem(fittedStem: FittedStem): void {
    logValidation(
        joinIndented(2)([
            'Fitted-stem computed:',
            'transform:',
            ...indent(2)(formatMatrixBasis(fittedStem.transform, { precision: 5 })),
            'stem:',
            ...indent(2)(formatMatrixBasis(fittedStem.stemLocal, { precision: 5 })),
            'head:',
            ...indent(2)(formatMatrixBasis(fittedStem.headLocal, { precision: 5 })),
            'bearing:',
            ...indent(2)(
                fittedStem.bearingLocal
                    ? formatMatrixBasis(fittedStem.bearingLocal, { precision: 5 })
                    : ['null'],
            ),
            ...indent(2)(_formatFittedStemAxis('neck-axis', fittedStem.axesLocal?.neckAxis)),
            ...indent(2)(_formatFittedStemAxis('pa-axis', fittedStem.axesLocal?.paAxis)),
            ...indent(2)(_formatFittedStemAxis('shaft-axis', fittedStem.axesLocal?.shaftAxis)),
            'resection-plane:',
            `  origin: ${formatVector(fittedStem.resectionPlaneLocal.origin)}`,
            `  normal: ${formatVector(fittedStem.resectionPlaneLocal.normal)}`,
            `  x: ${formatVector(fittedStem.resectionPlaneLocal.x)}`,
            `  y: ${formatVector(fittedStem.resectionPlaneLocal.y)}`,
        ]),
    );
}

function _formatFittedStemAxis(name: string, axis?: FittedStemAxis): string[] {
    return axis
        ? [
              name,
              `  position: ${formatVector(axis.position)}`,
              `  direction: ${formatVector(axis.direction)}`,
          ]
        : [`${name}: undefined`];
}

/**
 * Aligns the bearing with the head. The API aligns it with the liner, and the head transform
 * cannot be reused: a head's origin is its centre, a bearing's is its bottom hole centre, so that
 * offset comes out along the neck axis.
 */
function computeBearingTransform(apiStem: ApiFittedStem, apiCup: ApiFittedCup): Matrix4 {
    const bearing = assertNonNull(apiCup.bearing, 'fitted-cup-bearing');
    const apiBearingTransform = matrixFromApi(bearing.tmatrix);

    const apiHeadTransform = matrixFromApi(apiStem.head.tmatrix);
    const apiHeadCentre = positionalPart(apiHeadTransform);

    const apiBearingHeadCentre = vector3(bearing.head_centre);
    const shift = apiHeadCentre.clone().sub(apiBearingHeadCentre);
    const translationTransform = new Matrix4().makeTranslation(shift);
    const shiftedBearingTransform = translationTransform.clone().multiply(apiBearingTransform);

    const apiStemNeckAxis = vector3(apiStem.neck_axis);
    apiStemNeckAxis.normalize();
    const apiBearingCentreLineAxis = vector3(bearing.centre_line_axis);
    apiBearingCentreLineAxis.normalize();

    const axis = apiBearingCentreLineAxis.clone().cross(apiStemNeckAxis);
    axis.normalize();
    const angle = apiBearingCentreLineAxis.angleTo(apiStemNeckAxis);
    const rotationTransform = new Matrix4().makeRotationAxis(axis, angle);

    const translationToHeadCentre = new Matrix4().makeTranslation(apiHeadCentre);
    const rotationAroundHeadCentre = translationToHeadCentre
        .clone()
        .multiply(rotationTransform.clone().multiply(translationToHeadCentre.clone().invert()));

    return rotationAroundHeadCentre.clone().multiply(shiftedBearingTransform);
}
